import { motion } from "framer-motion";
import { ArrowRight, Eye, EyeOff, Loader2, Lock, Mail } from "lucide-react";
import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import PublicFooter from "../components/layout/PublicFooter";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const DEMO_ACCOUNTS = [
    { email: "admin@shariti.com", label: "Back Office RH" },
    { email: "sarah@shariti.com", label: "Pré-recruteur" },
    { email: "marc@shariti.com", label: "Référent terrain" },
];

export default function Login() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [isProd, setIsProd] = useState(true); // default to hidden — fail safe

    useEffect(() => {
        // Tiny public endpoint — no auth required
        api.get("/config")
            .then((r) => setIsProd(!!r.data?.is_prod))
            .catch(() => setIsProd(true)); // fail-safe: hide demo helpers if config fetch fails
    }, []);

    if (user) return <Navigate to="/dashboard" replace />;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await login(email.trim().toLowerCase(), password);
            toast.success("Bienvenue dans PREREC");
            navigate("/dashboard");
        } catch (err) {
            // iter110g · CEO 2026-09-16 — jusqu'ici, TOUTE erreur (401 réel,
            // timeout réseau, 503, CORS…) affichait "Identifiants invalides"
            // ce qui a fait croire à un bug d'auth alors que c'était de
            // l'infra bloquée. On distingue maintenant clairement :
            //   • 401 avec detail explicite → afficher le detail
            //   • pas de réponse serveur → message "Serveur momentanément indisponible"
            //   • 5xx → message "Serveur indisponible"
            const status = err.response?.status;
            const detail = err.response?.data?.detail;
            if (status === 401 && detail) {
                toast.error(detail);
            } else if (!err.response) {
                toast.error("Serveur momentanément indisponible", {
                    description: "Réessayez dans 5 secondes — vos identifiants sont probablement bons.",
                    duration: 6000,
                });
            } else if (status >= 500) {
                toast.error(`Serveur indisponible (${status})`, {
                    description: "Le service redémarre peut-être. Réessayez dans 30 secondes.",
                    duration: 6000,
                });
            } else {
                toast.error(detail || "Identifiants invalides");
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-shariti-cream">
            <div className="flex-1 grid lg:grid-cols-2">
            {/* Left: form */}
            <div className="flex items-center justify-center p-6 md:p-12">
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="w-full max-w-md"
                >
                    <div className="flex items-center gap-3 mb-10" data-testid="login-logo">
                        <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-shariti-orange to-shariti-orange-light flex items-center justify-center shadow-btn">
                            <span className="text-white font-black text-lg">P</span>
                        </div>
                        <div>
                            <div className="text-2xl font-black tracking-tight text-shariti-black leading-none">
                                PREREC
                            </div>
                            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mt-1">
                                Le cockpit du recrutement terrain
                            </div>
                        </div>
                    </div>

                    <h1 className="text-3xl md:text-4xl font-black tracking-tight text-shariti-black">
                        Connectez-vous au cockpit.
                    </h1>
                    <p className="mt-3 text-sm font-medium text-slate-500">
                        Priorisez les meilleurs profils, scorez en live et déclenchez l'immersion en quelques secondes.
                    </p>

                    <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="login-form">
                        <label className="block">
                            <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Email</span>
                            <div className="mt-1.5 relative">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                <input
                                    data-testid="login-email-input"
                                    required
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full h-12 rounded-2xl border border-shariti-gray bg-white pl-11 pr-4 text-sm font-medium text-shariti-black placeholder:text-slate-400 outline-none focus:border-shariti-orange focus:ring-4 focus:ring-shariti-orange/10 transition"
                                    placeholder="vous@shariti.com"
                                />
                            </div>
                        </label>
                        <label className="block">
                            <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Mot de passe</span>
                            <div className="mt-1.5 relative">
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                <input
                                    data-testid="login-password-input"
                                    required
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full h-12 rounded-2xl border border-shariti-gray bg-white pl-11 pr-12 text-sm font-medium text-shariti-black placeholder:text-slate-400 outline-none focus:border-shariti-orange focus:ring-4 focus:ring-shariti-orange/10 transition"
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((v) => !v)}
                                    data-testid="login-password-toggle"
                                    aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 h-8 w-8 inline-flex items-center justify-center rounded-full text-slate-400 hover:text-shariti-orange hover:bg-shariti-cream transition"
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </label>

                        <button
                            data-testid="login-submit-button"
                            disabled={submitting}
                            className="w-full h-12 rounded-2xl bg-shariti-orange text-white font-bold inline-flex items-center justify-center gap-2 hover:bg-[#d66528] shadow-btn transition active:scale-[0.98] disabled:opacity-60"
                        >
                            {submitting ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <>
                                    Entrer dans le cockpit
                                    <ArrowRight className="h-4 w-4" />
                                </>
                            )}
                        </button>
                    </form>

                    {!isProd && (
                        <div className="mt-8">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">
                                Démo rapide
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {DEMO_ACCOUNTS.map((a) => (
                                    <button
                                        key={a.email}
                                        data-testid={`login-demo-${a.email}`}
                                        onClick={() => {
                                            setEmail(a.email);
                                            setPassword("Prerec2026!");
                                        }}
                                        className="text-xs font-bold px-3 py-1.5 rounded-full bg-white border border-shariti-gray text-slate-600 hover:border-shariti-orange hover:text-shariti-orange transition"
                                    >
                                        {a.label}
                                    </button>
                                ))}
                            </div>
                            <p className="mt-3 text-[11px] font-medium text-slate-400">
                                Mot de passe :{" "}
                                <span className="font-bold text-slate-600">Prerec2026!</span>
                            </p>
                        </div>
                    )}
                </motion.div>
            </div>

            {/* Right: brand panel */}
            <div className="relative hidden lg:flex items-center justify-center p-12 overflow-hidden bg-gradient-to-br from-[#181818] via-[#231915] to-[#3b2010]">
                <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-shariti-orange/30 blur-3xl" />
                <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-shariti-yellow/20 blur-3xl" />
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.6 }}
                    className="relative z-10 max-w-md text-white"
                >
                    <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest border border-white/20">
                        <span className="h-1.5 w-1.5 rounded-full bg-shariti-orange" /> Cockpit · Scoring · Action
                    </div>
                    <h2 className="mt-6 text-4xl xl:text-5xl font-black tracking-tight leading-[1.05]">
                        Détectez les talents{" "}
                        <span className="text-shariti-orange">avant tout le monde.</span>
                    </h2>
                    <p className="mt-5 text-base font-medium text-white/70 leading-relaxed">
                        PREREC priorise vos candidatures, attribue un score, structure vos
                        appels et accélère vos décisions de recrutement.
                    </p>
                    <div className="mt-10 grid grid-cols-3 gap-4">
                        {[
                            { k: "92", l: "Score moyen pépite" },
                            { k: "3×", l: "Vitesse de tri" },
                            { k: "27", l: "Critères évalués" },
                        ].map((s) => (
                            <div
                                key={s.l}
                                className="rounded-2xl bg-white/5 backdrop-blur-md border border-white/10 p-4"
                            >
                                <div className="text-2xl font-black tracking-tight text-shariti-orange">
                                    {s.k}
                                </div>
                                <div className="text-[10px] font-bold uppercase tracking-widest text-white/60 mt-1">
                                    {s.l}
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>
            </div>
            </div>
            <PublicFooter />
        </div>
    );
}
